import {FC, useEffect, useState} from 'react'
import {api, ApiError, FlyerContent, FlyerData, listingImageSrc} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {useToast} from '../../components/Toast'
import {PageHead} from '../../components/PageHead'
import {FlyerPoster} from '../../components/FlyerPoster'
import {exportFlyerPng, exportFlyerPdf} from '../../components/flyerExport'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const PUBLIC_PATH = '/flyer/live'

const lines = (v: string[]) => v.join('\n')
const splitLines = (v: string): string[] => v.split('\n').map((s) => s.trim()).filter(Boolean)

const FlyerPage: FC = () => {
  const toast = useToast()
  const [data, setData] = useState<FlyerData | null>(null)
  const [form, setForm] = useState<FlyerContent | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [busy, setBusy] = useState<'png' | 'pdf' | null>(null)
  const [uploading, setUploading] = useState<'hero' | 'keys' | 'photos' | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const d = await api.publicFlyer()
      setData(d)
      setForm(d.settings.content)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to load flyer')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const set = <K extends keyof FlyerContent>(k: K, v: FlyerContent[K]) =>
    setForm((f) => (f ? {...f, [k]: v} : f))

  const save = async () => {
    if (!form) return
    setSaving(true)
    try {
      const settings = await api.updateFlyerSettings({content: form})
      setData((d) => (d ? {...d, settings} : d))
      toast.success('Flyer saved.')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not save')
    } finally {
      setSaving(false)
    }
  }

  const uploadHero = async (file: File) => {
    setUploading('hero')
    try {
      const {key} = await api.uploadFile(file)
      set('heroImage', key)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Upload failed')
    } finally {
      setUploading(null)
    }
  }
  const uploadKeys = async (file: File) => {
    setUploading('keys')
    try {
      const {key} = await api.uploadFile(file)
      set('keysImage', key)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Upload failed')
    } finally {
      setUploading(null)
    }
  }
  const uploadPhotos = async (files: FileList) => {
    if (!form) return
    setUploading('photos')
    try {
      const keys: string[] = []
      for (const f of Array.from(files).slice(0, 4 - form.photos.length)) {
        const {key} = await api.uploadFile(f)
        keys.push(key)
      }
      set('photos', [...form.photos, ...keys].slice(0, 4))
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Upload failed')
    } finally {
      setUploading(null)
    }
  }

  const node = () => document.getElementById('flyer-poster') as HTMLElement | null
  const doExport = async (kind: 'png' | 'pdf') => {
    const n = node()
    if (!n) return
    setBusy(kind)
    try {
      if (kind === 'png') await exportFlyerPng(n)
      else await exportFlyerPdf(n)
    } catch {
      toast.error('Export failed. Try again.')
    } finally {
      setBusy(null)
    }
  }

  const publicUrl = `${window.location.origin}${PUBLIC_PATH}`
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl)
      toast.success('Public link copied.')
    } catch {
      toast.error('Could not copy link.')
    }
  }

  return (
    <div>
      <PageHead icon='✈️' eyebrow='Marketing' title='Flyer Automation'
        sub='A promotional flyer with live bed availability — share the link or export an image or PDF.' />

      {/* Action toolbar */}
      <div className='ccp-fade-up' style={{display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', background: '#fff', borderRadius: 14, padding: '12px 14px', boxShadow: '0 6px 18px rgba(27,27,41,0.06)', marginBottom: 22}}>
        <button onClick={copyLink} style={btnPrimary}>🔗 Copy public link</button>
        <a href={PUBLIC_PATH} target='_blank' rel='noreferrer' style={{...btnGhost, textDecoration: 'none'}}>Open live page ↗</a>
        <div style={{marginLeft: 'auto', display: 'flex', gap: 10, flexWrap: 'wrap'}}>
          <button onClick={() => doExport('png')} disabled={!!busy || loading} style={btnGhost}>{busy === 'png' ? <Spinner size={13} /> : '⬇ PNG'}</button>
          <button onClick={() => doExport('pdf')} disabled={!!busy || loading} style={btnGhost}>{busy === 'pdf' ? <Spinner size={13} /> : '⬇ PDF'}</button>
        </div>
      </div>

      {loading || !form || !data ? (
        <div style={{padding: 40, textAlign: 'center', color: MUTED}}>Loading flyer…</div>
      ) : (
        <div className='row g-4'>
          {/* Editor */}
          <div className='col-12 col-xl-5'>
            <div className='card' style={{borderRadius: 16, border: 'none', boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>
              <div className='card-body'>
                <h3 style={{fontSize: 16, fontWeight: 800, color: INK, margin: '0 0 4px'}}>Flyer content</h3>
                <p style={{fontSize: 13, color: MUTED, margin: '0 0 18px'}}>Bed counts and "{data.totals.availableBeds} beds available" update live from the inventory.</p>

                <div style={{display: 'flex', gap: 12}}>
                  <Field label='Airport code'><input style={inp} value={form.airportCode} onChange={(e) => set('airportCode', e.target.value)} /></Field>
                  <Field label='City'><input style={inp} value={form.city} onChange={(e) => set('city', e.target.value)} /></Field>
                </div>
                <Field label='Badges (one per line)'>
                  <textarea style={{...inp, minHeight: 70}} value={lines(form.badges)} onChange={(e) => set('badges', splitLines(e.target.value))} />
                </Field>

                <Label>Monthly bed options</Label>
                {form.bedOptions.map((o, i) => (
                  <div key={i} style={{display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center'}}>
                    <select style={{...inp, flex: '0 0 90px'}} value={o.type} onChange={(e) => set('bedOptions', form.bedOptions.map((x, idx) => idx === i ? {...x, type: e.target.value as FlyerContent['bedOptions'][number]['type']} : x))}>
                      <option value='TWIN'>Twin</option>
                      <option value='FULL'>Full</option>
                      <option value='QUEEN'>Queen</option>
                    </select>
                    <input style={{...inp, flex: 2}} placeholder='Label' value={o.label} onChange={(e) => set('bedOptions', form.bedOptions.map((x, idx) => idx === i ? {...x, label: e.target.value} : x))} />
                    <input style={{...inp, flex: 1}} placeholder='$350' value={o.price} onChange={(e) => set('bedOptions', form.bedOptions.map((x, idx) => idx === i ? {...x, price: e.target.value} : x))} />
                    <button onClick={() => set('bedOptions', form.bedOptions.filter((_, idx) => idx !== i))} title='Remove' style={iconBtn}>×</button>
                  </div>
                ))}
                <button onClick={() => set('bedOptions', [...form.bedOptions, {type: 'TWIN', label: 'Twin Size Beds', price: '$0'}])} style={{...btnGhost, marginBottom: 14}}>+ Add option</button>
                <div style={{display: 'flex', gap: 12}}>
                  <Field label='Price suffix'><input style={inp} value={form.priceSuffix} onChange={(e) => set('priceSuffix', e.target.value)} /></Field>
                  <Field label='Deposit amount'><input style={inp} value={form.depositAmount} onChange={(e) => set('depositAmount', e.target.value)} /></Field>
                </div>

                <Field label='Amenities — “Why you’ll love it” (one per line)'>
                  <textarea style={{...inp, minHeight: 120}} value={lines(form.amenities)} onChange={(e) => set('amenities', splitLines(e.target.value))} />
                </Field>
                <Field label='Transit heading'><input style={inp} value={form.transitTitle} onChange={(e) => set('transitTitle', e.target.value)} /></Field>
                <Field label='Transit lines (one per line)'>
                  <textarea style={{...inp, minHeight: 80}} value={lines(form.transit)} onChange={(e) => set('transit', splitLines(e.target.value))} />
                </Field>
                <Field label='More amenities — after transit (one per line)'>
                  <textarea style={{...inp, minHeight: 70}} value={lines(form.amenitiesExtra)} onChange={(e) => set('amenitiesExtra', splitLines(e.target.value))} />
                </Field>
                <Field label='Footer tagline'><input style={inp} value={form.footerText} onChange={(e) => set('footerText', e.target.value)} /></Field>

                {/* Hero image */}
                <Label>Hero photo</Label>
                <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14}}>
                  {form.heroImage && <img src={listingImageSrc(form.heroImage)} alt='' style={{width: 96, height: 60, borderRadius: 8, objectFit: 'cover', border: '1px solid #e5e7eb'}} />}
                  <label style={{...btnGhost, display: 'inline-flex', alignItems: 'center', gap: 8, cursor: uploading === 'hero' ? 'wait' : 'pointer'}}>
                    {uploading === 'hero' ? <Spinner size={13} /> : '＋'} {form.heroImage ? 'Replace' : 'Upload'}
                    <input type='file' accept='image/*' hidden disabled={uploading === 'hero'} onChange={(e) => {const f = e.target.files?.[0]; if (f) void uploadHero(f); e.target.value = ''}} />
                  </label>
                  {form.heroImage && <button onClick={() => set('heroImage', null)} style={iconBtn}>×</button>}
                </div>

                {/* Keys badge photo */}
                <Label>“Pick up your keys today!” badge photo</Label>
                <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14}}>
                  {form.keysImage && <img src={listingImageSrc(form.keysImage)} alt='' style={{width: 96, height: 60, borderRadius: 8, objectFit: 'cover', border: '1px solid #e5e7eb'}} />}
                  <label style={{...btnGhost, display: 'inline-flex', alignItems: 'center', gap: 8, cursor: uploading === 'keys' ? 'wait' : 'pointer'}}>
                    {uploading === 'keys' ? <Spinner size={13} /> : '＋'} {form.keysImage ? 'Replace' : 'Upload'}
                    <input type='file' accept='image/*' hidden disabled={uploading === 'keys'} onChange={(e) => {const f = e.target.files?.[0]; if (f) void uploadKeys(f); e.target.value = ''}} />
                  </label>
                  {form.keysImage && <button onClick={() => set('keysImage', null)} style={iconBtn}>×</button>}
                </div>

                {/* Photo strip */}
                <Label>Photo strip (up to 4)</Label>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10}}>
                  {form.photos.map((p, i) => (
                    <div key={`${p}-${i}`} style={{position: 'relative', width: 84, height: 60, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb'}}>
                      <img src={listingImageSrc(p)} alt='' style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                      <button onClick={() => set('photos', form.photos.filter((_, idx) => idx !== i))} title='Remove' style={{position: 'absolute', top: 2, right: 2, width: 20, height: 20, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer', fontSize: 13, lineHeight: 1}}>×</button>
                    </div>
                  ))}
                  {form.photos.length < 4 && (
                    <label style={{width: 84, height: 60, borderRadius: 8, border: '1px dashed #cbd2dc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, cursor: uploading === 'photos' ? 'wait' : 'pointer'}}>
                      {uploading === 'photos' ? <Spinner size={13} /> : '＋'}
                      <input type='file' accept='image/*' multiple hidden disabled={uploading === 'photos'} onChange={(e) => {if (e.target.files?.length) void uploadPhotos(e.target.files); e.target.value = ''}} />
                    </label>
                  )}
                </div>

                <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 10, paddingTop: 14, borderTop: '1px solid #f1f3f7'}}>
                  <button onClick={save} disabled={saving} style={{...btnPrimary, minWidth: 130}}>{saving ? <Spinner size={14} /> : 'Save flyer'}</button>
                </div>
              </div>
            </div>
          </div>

          {/* Live preview */}
          <div className='col-12 col-xl-7'>
            <div style={{fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10}}>Live preview <span style={{textTransform: 'none', fontWeight: 500}}>· scroll to see the full width</span></div>
            <div style={{overflowX: 'auto', display: 'flex', justifyContent: 'flex-start', paddingBottom: 8}}>
              <FlyerPoster data={{...data, settings: {...data.settings, content: form}}} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const Field: FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
  <div style={{flex: 1, minWidth: 140, marginBottom: 14}}>
    <label style={{display: 'block', fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6}}>{label}</label>
    {children}
  </div>
)
const Label: FC<{children: React.ReactNode}> = ({children}) => (
  <div style={{fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em', margin: '4px 0 8px'}}>{children}</div>
)

const btnBase: React.CSSProperties = {fontSize: 13, fontWeight: 600, borderRadius: 8, padding: '9px 14px', cursor: 'pointer', border: '1px solid transparent'}
const btnPrimary: React.CSSProperties = {...btnBase, background: ACCENT, color: '#fff'}
const btnGhost: React.CSSProperties = {...btnBase, background: '#fff', border: '1px solid #e5e7eb', color: INK}
const iconBtn: React.CSSProperties = {flex: '0 0 auto', width: 34, height: 34, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#d9214e', cursor: 'pointer', fontSize: 16}
const inp: React.CSSProperties = {width: '100%', padding: '9px 11px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, color: INK, background: '#fff', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit'}

export {FlyerPage}
